import React, { useState, useEffect } from "react";
import { ShieldCheck, X, Eye, PencilLine } from "lucide-react";
import { api, fmtINR } from "../../lib/api";
import { Checkbox } from "../ui/checkbox";

// Canonical sharable modules — mirrors backend ADVISOR_MODULES.
export const ADVISOR_MODULE_OPTIONS = [
  { key: "dashboard", label: "Dashboard / Net Worth" },
  { key: "assets", label: "Assets" },
  { key: "liabilities", label: "Liabilities" },
  { key: "insurance", label: "Insurance" },
  { key: "goals", label: "Goals" },
  { key: "documents", label: "Documents" },
];

// Modules whose individual records can be selectively shared.
const RECORD_PICK = {
  assets: {
    url: "/assets",
    key: "assets",
    label: (r) => r.name,
    sub: (r) => fmtINR(r.value),
  },
  liabilities: {
    url: "/liabilities",
    key: "liabilities",
    label: (r) => r.name,
    sub: (r) => fmtINR(r.outstanding),
  },
  insurance: {
    url: "/insurance",
    key: "policies",
    label: (r) => r.name,
    sub: (r) => r.policy_type,
  },
  goals: {
    url: "/goals",
    key: "goals",
    label: (r) => r.name,
    sub: (r) => fmtINR(r.target_amount),
  },
  documents: {
    url: "/vault/documents",
    key: "documents",
    label: (r) => r.title,
    sub: (r) => r.category || "",
  },
};

/**
 * Confirmation popup shown when a user is about to connect with a Personal
 * Finance Advisor.
 *
 * Modes:
 *   mode="simple"  (registration): a brand-new user has no data yet, so we
 *     skip all pickers — everything is shared in View + Edit mode and we just
 *     confirm. They refine later from Settings → Access Management.
 *   mode="full"    (existing user): everything is selected by DEFAULT and the
 *     user deselects what they don't want; per-record pickers available.
 *
 * Props:
 *   advisor   = { display_name, firm_name, advisor_code, bio }
 *   initialModules / initialPermission (optional, for editing)
 *   allowRecordSelection  — show per-record pickers (authenticated flow only)
 *   confirmLabel
 *   onConfirm({ modules, permission_level, shared_ids })
 *   onCancel()
 */
export default function AdvisorConnectModal({
  advisor,
  mode = "full",
  initialModules,
  initialPermission = "suggest",
  allowRecordSelection = false,
  confirmLabel = "Connect with advisor",
  onConfirm,
  onCancel,
}) {
  const simple = mode === "simple";
  const [modules, setModules] = useState(() => {
    const base = {};
    ADVISOR_MODULE_OPTIONS.forEach((m) => {
      // Default: share everything (opt-out). For editing, honour initialModules.
      base[m.key] = initialModules ? !!initialModules[m.key] : true;
    });
    return base;
  });
  // { moduleKey: ids[] | undefined }. undefined/null => share all of that module.
  const [selection, setSelection] = useState({});
  const [permission, setPermission] = useState(
    simple ? "suggest" : initialPermission,
  );
  const [submitting, setSubmitting] = useState(false);

  const anySelected = Object.values(modules).some(Boolean);
  const toggle = (key) => setModules((m) => ({ ...m, [key]: !m[key] }));
  const setScope = (key, ids) => setSelection((s) => ({ ...s, [key]: ids }));

  const submit = async () => {
    setSubmitting(true);
    try {
      // Build shared_ids only for record-level modules limited to a selection.
      const shared_ids = {};
      if (allowRecordSelection) {
        Object.keys(RECORD_PICK).forEach((k) => {
          if (modules[k] && Array.isArray(selection[k]))
            shared_ids[k] = selection[k];
        });
      }
      await onConfirm({ modules, permission_level: permission, shared_ids });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden">
        {/* HEADER */}
        <div className="relative bg-gradient-to-r from-emerald-600 to-blue-600 p-6 text-white">
          <button
            onClick={onCancel}
            className="absolute right-4 top-4 grid h-8 w-8 place-items-center rounded-lg bg-white/15 hover:bg-white/25"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest opacity-90">
            <ShieldCheck className="h-4 w-4" />
            Connect with advisor
          </div>
          <h2 className="mt-2 font-display text-2xl font-bold">
            {advisor?.display_name || "Personal Finance Advisor"}
          </h2>
          {advisor?.firm_name && (
            <p className="text-sm text-white/85">{advisor.firm_name}</p>
          )}
          {advisor?.advisor_code && (
            <p className="mt-1 text-xs font-mono text-white/70">
              {advisor.advisor_code}
            </p>
          )}
        </div>

        {/* BODY */}
        <div className="max-h-[60vh] overflow-y-auto p-6 space-y-6">
          {simple ? (
            <div className="rounded-2xl border border-emerald-200 dark:border-emerald-500/20 bg-emerald-50/60 dark:bg-emerald-500/10 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800 dark:text-emerald-200">
                <ShieldCheck className="h-4 w-4" />
                All your data will be shared in View + Edit mode
              </div>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                {advisor?.display_name || "Your advisor"} will be able to see
                all your modules and propose add / edit / delete changes —{" "}
                <b>nothing is applied until you approve it</b>. You can
                fine-tune exactly what's shared anytime from{" "}
                <b>Settings → Access Management</b>.
              </p>
            </div>
          ) : (
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Everything is shared by default — just{" "}
              <b>deselect anything you don't want</b> this advisor to see. You
              can change or revoke access anytime from{" "}
              <b>Settings → Access Management</b>.
            </p>
          )}

          {!simple && (
            <>
              {/* MODULES */}
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                  Modules to share
                </div>
                <div className="space-y-2">
                  {ADVISOR_MODULE_OPTIONS.map((m) => {
                    const on = modules[m.key];
                    const cfg = allowRecordSelection
                      ? RECORD_PICK[m.key]
                      : null;
                    const sel = selection[m.key];
                    const isSel = Array.isArray(sel);
                    return (
                      <div
                        key={m.key}
                        className={`rounded-xl border transition ${
                          on
                            ? "border-emerald-400 bg-emerald-50/60 dark:bg-emerald-500/10"
                            : "border-slate-200 dark:border-slate-700"
                        }`}
                      >
                        <label className="flex items-center gap-3 p-3 cursor-pointer">
                          <Checkbox
                            checked={on}
                            onCheckedChange={() => toggle(m.key)}
                          />
                          <span className="flex-1 text-sm font-medium text-slate-800 dark:text-slate-200">
                            {m.label}
                          </span>
                          {on && cfg && (
                            <span className="text-[11px] text-slate-400">
                              {isSel ? `${sel.length} selected` : "all"}
                            </span>
                          )}
                        </label>
                        {on && cfg && (
                          <div className="px-3 pb-3">
                            <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700 p-0.5 text-xs">
                              <Seg
                                active={!isSel}
                                onClick={() => setScope(m.key, null)}
                              >
                                Share all
                              </Seg>
                              <Seg
                                active={isSel}
                                onClick={() => setScope(m.key, sel || [])}
                              >
                                Choose specific
                              </Seg>
                            </div>
                            {isSel && (
                              <ConnectRecordPicker
                                cfg={cfg}
                                selected={sel}
                                onChange={(ids) => setScope(m.key, ids)}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* PERMISSION */}
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                  Permission level
                </div>
                <div className="space-y-2">
                  <PermissionOption
                    active={permission === "view"}
                    onClick={() => setPermission("view")}
                    icon={Eye}
                    title="View Only"
                    desc="The advisor can see the shared modules but cannot propose any changes."
                  />
                  <PermissionOption
                    active={permission === "suggest"}
                    onClick={() => setPermission("suggest")}
                    icon={PencilLine}
                    title="View + Edit"
                    desc="The advisor can propose add/edit/delete changes. Nothing is applied until you approve it."
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* FOOTER */}
        <div className="p-6 border-t border-slate-200 dark:border-slate-800 flex gap-3">
          <button onClick={onCancel} className="btn-secondary flex-1">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!anySelected || submitting}
            className="btn-primary flex-1 disabled:opacity-50"
          >
            {submitting ? "Saving…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function Seg({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1 rounded-md font-medium transition ${
        active
          ? "bg-emerald-500 text-white"
          : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
      }`}
    >
      {children}
    </button>
  );
}

function ConnectRecordPicker({ cfg, selected, onChange }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(cfg.url);
        if (alive) setItems(data[cfg.key] || []);
      } catch (_) {
        if (alive) setItems([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [cfg.url, cfg.key]);

  if (items === null)
    return <div className="text-xs text-slate-400 mt-2">Loading records…</div>;
  if (!items.length)
    return (
      <div className="text-xs text-slate-400 mt-2">No records to share.</div>
    );

  const sel = new Set((selected || []).map(String));
  const toggle = (id) => {
    const n = new Set(sel);
    n.has(String(id)) ? n.delete(String(id)) : n.add(String(id));
    onChange([...n]);
  };

  return (
    <div className="mt-2 space-y-1 max-h-48 overflow-y-auto rounded-lg border border-slate-100 dark:border-slate-800 p-1">
      {items.map((it) => (
        <label
          key={it.id}
          className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-white dark:hover:bg-slate-800 cursor-pointer"
        >
          <Checkbox
            checked={sel.has(String(it.id))}
            onCheckedChange={() => toggle(it.id)}
          />
          <span className="flex-1 text-sm text-slate-800 dark:text-slate-200 truncate">
            {cfg.label(it)}
          </span>
          <span className="text-xs text-slate-400 shrink-0">{cfg.sub(it)}</span>
        </label>
      ))}
    </div>
  );
}

function PermissionOption({ active, onClick, icon: Icon, title, desc }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left flex items-start gap-3 rounded-xl border p-3 transition ${
        active
          ? "border-emerald-400 bg-emerald-50 dark:bg-emerald-500/10"
          : "border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
      }`}
    >
      <Icon className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400 mt-0.5" />
      <div>
        <div className="text-sm font-semibold text-slate-900 dark:text-white">
          {title}
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400">{desc}</div>
      </div>
    </button>
  );
}
